import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { chatApi, createRealtime } from "../lib/realtime";
import { getPermission, isDocumentHidden, showNotification } from "../lib/notifications";
import { toast } from "sonner";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { status, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [realtimeMode, setRealtimeMode] = useState("connecting");
  const [presence, setPresence] = useState({});
  // typing: {conv_id: {user_id: {display_name, ondatag, ts}}}
  const [typing, setTyping] = useState({});
  const listenersRef = useRef(new Set());
  const readListenersRef = useRef(new Set());
  const pulseListenersRef = useRef(new Set());
  const activeConvIdRef = useRef(null);
  const activeConvFocusedRef = useRef(true);
  const conversationsRef = useRef([]);
  const rtRef = useRef(null);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const refreshConversations = useCallback(async () => {
    if (status !== "authed") return;
    try {
      const data = await chatApi.listConversations();
      setConversations(data);
      setPresence((prev) => {
        const next = { ...prev };
        for (const c of data) {
          if (c.other) {
            next[c.other.id] = {
              is_online: !!c.other.is_online,
              last_seen: c.other.last_seen || null,
            };
          }
          if (c.members) {
            for (const m of c.members) {
              next[m.id] = {
                is_online: !!m.is_online,
                last_seen: m.last_seen || null,
              };
            }
          }
        }
        return next;
      });
    } catch (_e) {
      /* ignore */
    }
  }, [status]);

  useEffect(() => {
    if (status !== "authed") {
      setConversations([]);
      setPresence({});
      setTyping({});
      return;
    }
    setLoadingConvs(true);
    refreshConversations().finally(() => setLoadingConvs(false));
  }, [status, refreshConversations]);

  const notifyIncomingMessage = useCallback(
    (msg) => {
      if (!user || msg.sender_id === user.id) return;
      const activeId = activeConvIdRef.current;
      const isActiveAndFocused =
        activeId === msg.conversation_id && !isDocumentHidden() && activeConvFocusedRef.current;
      if (isActiveAndFocused) return; // user is looking at this chat
      const conv = conversationsRef.current.find((c) => c.id === msg.conversation_id);
      const sender = conv?.members?.find((m) => m.id === msg.sender_id) || conv?.other;
      const senderName = sender?.display_name || "Mensaje nuevo";
      const title =
        conv?.type === "group" ? `${conv.title}` : senderName;
      const body =
        conv?.type === "group" && sender
          ? `${sender.display_name}: ${msg.text}`
          : msg.text;
      if (getPermission() === "granted" && isDocumentHidden()) {
        showNotification({
          title,
          body,
          tag: `conv-${msg.conversation_id}`,
          onClick: () => {
            window.location.hash = "";
            window.location.pathname = `/chat/${msg.conversation_id}`;
          },
        });
      } else if (isDocumentHidden() === false && activeId !== msg.conversation_id) {
        // App visible but user on another page — subtle in-app toast
        toast.message(title, { description: body.slice(0, 80) });
      }
    },
    [user],
  );

  const notifyVibra = useCallback(
    (evt) => {
      if (!user) return;
      if (evt.from_user_id === user.id) return; // your own vibra
      // Only notify if the current user is the AUTHOR of the mood pulse
      // We don't have that info in the WS event by default, so we skip precise author matching
      // and rely on: pulse.vibra is only interesting when the user is on a background tab.
      // Best-effort: fire a soft in-app or notification.
      if (getPermission() === "granted" && isDocumentHidden()) {
        showNotification({
          title: "Nueva vibra en tu Mood Wave",
          body: `Alguien envió vibras a tu OndaPulse — total: ${evt.vibras_count}`,
          tag: `vibra-${evt.pulse_id}`,
          onClick: () => {
            window.location.pathname = "/pulsos";
          },
        });
      }
    },
    [user],
  );

  useEffect(() => {
    if (status !== "authed") return undefined;
    const rt = createRealtime({
      onMessage: (msg) => {
        listenersRef.current.forEach((cb) => cb(msg));
        refreshConversations();
        notifyIncomingMessage(msg);
      },
      onRead: (evt) => {
        readListenersRef.current.forEach((cb) => cb(evt));
        refreshConversations();
      },
      onPresence: (evt) => {
        setPresence((prev) => ({
          ...prev,
          [evt.user_id]: { is_online: !!evt.is_online, last_seen: evt.last_seen || null },
        }));
      },
      onTyping: (evt) => {
        // Ignore my own typing echo (backend already filters, but safe)
        if (evt.user_id === user?.id) return;
        setTyping((prev) => {
          const conv = { ...(prev[evt.conversation_id] || {}) };
          conv[evt.user_id] = {
            ondatag: evt.ondatag,
            display_name: evt.display_name,
            ts: Date.now(),
          };
          return { ...prev, [evt.conversation_id]: conv };
        });
      },
      onConversationNew: (conv) => {
        setConversations((prev) => (prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev]));
      },
      onPulseEvent: (evt) => {
        pulseListenersRef.current.forEach((cb) => cb(evt));
        if (evt.type === "pulse.vibra") notifyVibra(evt);
      },
      onStatus: ({ mode }) => setRealtimeMode(mode),
      onPoll: async () => {
        await refreshConversations();
        const activeId = activeConvIdRef.current;
        if (activeId) {
          listenersRef.current.forEach((cb) => cb({ __poll: true, conversation_id: activeId }));
        }
        pulseListenersRef.current.forEach((cb) => cb({ __poll: true }));
      },
    });
    rtRef.current = rt;
    return () => {
      rtRef.current = null;
      rt.close();
    };
  }, [status, refreshConversations, notifyIncomingMessage, notifyVibra, user]);

  // Prune stale typing entries every 500ms
  useEffect(() => {
    const t = setInterval(() => {
      setTyping((prev) => {
        const now = Date.now();
        let changed = false;
        const next = {};
        for (const [cid, users] of Object.entries(prev)) {
          const kept = {};
          for (const [uid, info] of Object.entries(users)) {
            if (now - info.ts <= 4000) kept[uid] = info;
            else changed = true;
          }
          if (Object.keys(kept).length > 0) next[cid] = kept;
          else if (Object.keys(users).length > 0) changed = true;
        }
        return changed ? next : prev;
      });
    }, 500);
    return () => clearInterval(t);
  }, []);

  const subscribeToMessages = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const subscribeToReads = useCallback((cb) => {
    readListenersRef.current.add(cb);
    return () => readListenersRef.current.delete(cb);
  }, []);

  const subscribeToPulses = useCallback((cb) => {
    pulseListenersRef.current.add(cb);
    return () => pulseListenersRef.current.delete(cb);
  }, []);

  const setActiveConversationId = useCallback((id) => {
    activeConvIdRef.current = id;
  }, []);

  const setActiveConversationFocused = useCallback((focused) => {
    activeConvFocusedRef.current = !!focused;
  }, []);

  const sendTyping = useCallback((conversationId) => {
    if (!conversationId) return;
    rtRef.current && rtRef.current.send({ type: "typing", conversation_id: conversationId });
  }, []);

  const getPresenceFn = useCallback(
    (userId) => presence[userId] || { is_online: false, last_seen: null },
    [presence],
  );

  const refreshPresence = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const data = await chatApi.getPresence(userId);
      setPresence((prev) => ({
        ...prev,
        [userId]: { is_online: !!data.is_online, last_seen: data.last_seen || null },
      }));
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  const getTypingFor = useCallback(
    (conversationId) => Object.values(typing[conversationId] || {}),
    [typing],
  );

  const value = useMemo(
    () => ({
      conversations,
      loadingConvs,
      refreshConversations,
      subscribeToMessages,
      subscribeToReads,
      subscribeToPulses,
      setActiveConversationId,
      setActiveConversationFocused,
      sendTyping,
      getTypingFor,
      realtimeMode,
      me: user,
      getPresence: getPresenceFn,
      refreshPresence,
    }),
    [
      conversations,
      loadingConvs,
      refreshConversations,
      subscribeToMessages,
      subscribeToReads,
      subscribeToPulses,
      setActiveConversationId,
      setActiveConversationFocused,
      sendTyping,
      getTypingFor,
      realtimeMode,
      user,
      getPresenceFn,
      refreshPresence,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat debe usarse dentro de ChatProvider");
  return ctx;
}
